import { ShieldCheck, Award, Scale, Users, MapPin, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

export default function OverOns() {
  return (
    <div className="py-16" data-testid="over-ons-page">
      <Seo path="/over-ons" />
      <div className="max-w-4xl mx-auto px-6">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Over ons</span>
        <h1 className="font-display text-5xl sm:text-6xl font-bold text-ink mt-3 tracking-tight">
          Onafhankelijk. Voor jou.
        </h1>
        <p className="text-ink-soft text-lg mt-6 leading-relaxed max-w-2xl">
          CheckExpert is gemaakt door <b>Webtron BV</b>, een tech-bedrijf met basis in Waregem dat actief is in België en Nederland. We bouwden CheckExpert vanuit de overtuiging dat helderheid over wat in je document staat geen voorrecht hoort te zijn van wie zich dure specialisten kan veroorloven — of het nu gaat om huur, zorg, energie, werk of pensioen.
        </p>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {[
            { Icon: Scale, title: "Onafhankelijk", text: "Geen banden met aannemers, banken, verzekeraars of vakbonden. Geen commissies, geen affiliate-deals. Wat wij vinden, vertellen we." },
            { Icon: ShieldCheck, title: "Privacy first", text: "Documenten worden versleuteld geüpload, 30 dagen bewaard en daarna automatisch hard-deleted. Niet doorgegeven aan derden voor marketing." },
            { Icon: Users, title: "Voor particulieren", text: "We bedienen huurders, kopers, werknemers, gezinnen — niet de grote bedrijven. Onze prijzen weerspiegelen dat: €4,99 per analyse." },
            { Icon: Award, title: "Specialistische expertise", text: "Voor elk van onze 25 checks bouwden we een eigen juridisch kader, getoetst aan de BE/NL-wetgeving, waarmee onze AI gericht zoekt. Geen generieke samenvatting, maar een analyse op maat van het documenttype." },
          ].map(({ Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-border bg-white p-7">
              <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center text-[#002FA7]">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="font-display text-xl font-semibold text-ink mt-5">{title}</h3>
              <p className="text-ink-soft text-sm mt-2 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-white border border-border p-8" data-testid="founders-card">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Wie hierachter zit</span>
          <h2 className="font-display text-2xl font-semibold text-ink mt-2">De zaakvoerders van Webtron BV</h2>
          <p className="text-ink mt-3 leading-relaxed max-w-3xl">
            CheckExpert is gebouwd door <b>Heino Schietecatte</b> en <b>Glenn Vanlerberghe</b>, samen zaakvoerders van Webtron BV. Met meer dan twee decennia gecombineerde ervaring in ondernemen, tech en advies bouwden we het platform dat we zelf misten: een onafhankelijke, eerlijke en snelle manier om te weten wat er écht in een document staat — zonder commercieel belang.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div className="flex gap-5 items-start" data-testid="founder-heino">
              <div className="h-32 w-32 flex-shrink-0 rounded-2xl overflow-hidden bg-white border border-border shadow-sm">
                <picture>
                  <source srcSet="/team/heino-schietecatte.webp" type="image/webp" />
                  <img
                    src="/team/heino-schietecatte.jpg"
                    alt="Heino Schietecatte — Medeoprichter & CEO, Webtron BV"
                    width="128"
                    height="128"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </picture>
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-xl font-semibold text-ink">Heino Schietecatte</h3>
                <p className="text-ink-soft text-sm">Medeoprichter &amp; CEO</p>
                <p className="text-ink-soft text-sm mt-2 leading-relaxed">
                  Strategie, product en partnerships. Volgt nauwgezet hoe BE/NL-wetgeving evolueert en vertaalt dat naar bruikbare checks voor particulieren.
                </p>
              </div>
            </div>

            <div className="flex gap-5 items-start" data-testid="founder-glenn">
              <div className="h-32 w-32 flex-shrink-0 rounded-2xl overflow-hidden bg-white border border-border shadow-sm">
                <picture>
                  <source srcSet="/team/glenn-vanlerberghe.webp" type="image/webp" />
                  <img
                    src="/team/glenn-vanlerberghe.jpg"
                    alt="Glenn Vanlerberghe — Medeoprichter & CEO, Webtron BV"
                    width="128"
                    height="128"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </picture>
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-xl font-semibold text-ink">Glenn Vanlerberghe</h3>
                <p className="text-ink-soft text-sm">Medeoprichter &amp; CEO</p>
                <p className="text-ink-soft text-sm mt-2 leading-relaxed">
                  Operations, kwaliteit en klantenondersteuning. Zorgt dat elke analyse correct, snel en privacybewust verloopt — van upload tot rapport.
                </p>
              </div>
            </div>
          </div>

          <blockquote className="mt-8 pl-4 border-l-2 border-[#002FA7] text-ink leading-relaxed max-w-3xl">
            &ldquo;Mensen vragen al jaren offertes op zonder dat er een neutrale plek bestond om die te laten checken. Daarom bouwden we CheckExpert: in 60 seconden weet je wat er écht in je contract of offerte staat — eerlijk, onafhankelijk en zonder verkooppraat.&rdquo;
          </blockquote>
          <p className="text-ink-soft text-xs mt-2">— Heino &amp; Glenn</p>
        </div>

        <div className="mt-12 rounded-2xl bg-white border border-border p-8" data-testid="team-card">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Het team</span>
          <h2 className="font-display text-2xl font-semibold text-ink mt-2">Mensen die je dagelijks helpen</h2>
          <p className="text-ink-soft mt-3 leading-relaxed max-w-3xl">
            Achter elke analyse staat een team dat zorgt voor service, duidelijkheid en correcte begeleiding. Zij beantwoorden je vragen, vergelijken offertes en helpen je de juiste keuze maken.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div className="flex gap-5 items-start" data-testid="team-sophie">
              <div className="h-32 w-32 flex-shrink-0 rounded-2xl overflow-hidden bg-white border border-border shadow-sm">
                <picture>
                  <source srcSet="/team/sophie-vermeulen.webp" type="image/webp" />
                  <img
                    src="/team/sophie-vermeulen.jpg"
                    alt="Sophie Vermeulen — Klantenadviseur bij CheckExpert"
                    width="128"
                    height="128"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </picture>
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-xl font-semibold text-ink">Sophie Vermeulen</h3>
                <p className="text-ink-soft text-sm">Klantenadviseur · 25 jaar</p>
                <p className="text-ink-soft text-sm mt-2 leading-relaxed">
                  Sophie helpt bezoekers bij het vergelijken van offertes en contracten, beantwoordt vragen op een duidelijke manier en zorgt dat klanten snel de juiste keuze kunnen maken. Bekend om haar vriendelijke aanpak, nauwkeurigheid en klantgerichte service.
                </p>
              </div>
            </div>

            <div className="flex gap-5 items-start" data-testid="team-thomas">
              <div className="h-32 w-32 flex-shrink-0 rounded-2xl overflow-hidden bg-white border border-border shadow-sm">
                <picture>
                  <source srcSet="/team/thomas-de-wilde.webp" type="image/webp" />
                  <img
                    src="/team/thomas-de-wilde.jpg"
                    alt="Thomas De Wilde — Offertespecialist bij CheckExpert"
                    width="128"
                    height="128"
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </picture>
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-xl font-semibold text-ink">Thomas De Wilde</h3>
                <p className="text-ink-soft text-sm">Offertespecialist · 35 jaar</p>
                <p className="text-ink-soft text-sm mt-2 leading-relaxed">
                  Thomas analyseert aanvragen, vergelijkt offertes en ondersteunt klanten bij het vinden van de beste prijs-kwaliteitverhouding. Met zijn ervaring, praktische ingesteldheid en oog voor detail helpt hij dagelijks klanten om met vertrouwen een beslissing te nemen.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 rounded-2xl bg-slate-900 text-white p-10">
          <h2 className="font-display text-2xl font-bold tracking-tight">Onze missie</h2>
          <p className="mt-4 text-slate-300 leading-relaxed">
            "We willen dat elke huurder, werknemer, koper, patiënt of consument in 60 seconden weet of een document eerlijk is. Niet omdat we specialisten willen vervangen — wel omdat we vinden dat helderheid een basisrecht is, geen luxe."
          </p>
          <p className="mt-4 text-slate-400 text-sm">— Team CheckExpert</p>
        </div>

        <div className="mt-12 rounded-2xl bg-white border border-border p-8">
          <h3 className="font-display text-xl font-semibold text-ink">Contact &amp; vestiging</h3>
          <div className="mt-4 space-y-2 text-ink-soft text-sm">
            <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#002FA7]" /> Webtron BV · A. Rodenbachwijk 12, 8792 Waregem · België</p>
            <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#002FA7]" /> <a href="mailto:info@checkexpert.be" className="text-[#002FA7] font-semibold">info@checkexpert.be</a></p>
            <p className="text-xs">BTW BE0793944703</p>
          </div>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold"
            data-testid="over-ons-contact-cta"
          >
            Stuur ons een bericht
          </Link>
        </div>
      </div>
    </div>
  );
}
